/**
 * Case shell queries and mutations (17e, 20a). Every call goes through the browser-safe operator descriptors in
 * `@agentic/contracts` (`operatorApi`) with `ApiClient.call`. The case
 * page stays current through `GET /cases/{id}/events` (`useCaseStream`); its queries do not poll.
 */
import {
  type AnswerObligationBody,
  type AnswerObligationResponse,
  type AskAgainObligationBody,
  type CaseListQuery,
  type CaseState,
  type DecideObligationBody,
  type DeleteDocumentBody,
  ERROR_HTTP_STATUS,
  type HoldRequestInput,
  isPlatformError,
  type ObligationStatus,
  operatorApi,
  type StartCaseBody,
  type UpdateObligationBody,
  type VerifyDocumentBody,
  wave21OperatorRoutes,
} from '@agentic/contracts';
import {
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { client } from '../../app/api.ts';
import { downloadEvidence, uploadCaseDocument } from './documents.ts';
import type { UploadDocumentMeta } from './wave21-panels.tsx';

export const caseKeys = {
  all: ['cases'] as const,
  list: (filters: { state?: CaseState | undefined; processType?: string | undefined }) =>
    ['cases', 'list', filters] as const,
  detail: (caseId: string) => ['cases', caseId] as const,
  /** The detail with one agent activity's episode embedded (`?activity=`). */
  detailWith: (caseId: string, activationId: string | undefined) =>
    ['cases', caseId, 'detail', activationId ?? ''] as const,
  timeline: (caseId: string) => ['cases', caseId, 'timeline'] as const,
  workItems: (caseId: string) => ['cases', caseId, 'work-items'] as const,
  inbound: (caseId: string) => ['inbound', 'records', caseId] as const,
  definitions: ['processes', 'definitions'] as const,
  instance: (processInstanceId: string) => ['processes', 'instances', processInstanceId] as const,
  entitlements: ['entitlements', 'me'] as const,
  /** 26c: `GET /me/obligations` (all states share the prefix). */
  myObligations: (state: ObligationStatus = 'open') => ['me', 'obligations', state] as const,
};

export const caseListQuery = (filters: { state?: CaseState | undefined; processType?: string | undefined }) =>
  queryOptions({
    queryKey: caseKeys.list(filters),
    queryFn: ({ signal }) =>
      client.call(operatorApi.cases.list, {
        query: {
          ...(filters.state ? { state: filters.state } : {}),
          ...(filters.processType ? { processType: filters.processType } : {}),
          limit: 200,
        },
        signal,
      }),
    refetchInterval: 15_000,
  });

/** Filters of the case list page (review 1): view, text, domain, state and process type. */
export interface CaseListFilters {
  view: 'waiting' | 'mine' | 'all';
  q?: string | undefined;
  domain?: string | undefined;
  state?: CaseState | undefined;
  processType?: string | undefined;
}

/** Page size of the case list ("Load more" fetches the next page with the server's cursor). */
export const CASE_PAGE_SIZE = 50;

/** The case list as cursor pages (`GET /cases?…&cursor=`), refreshed every 15 s. */
export const caseListPagesQuery = (filters: CaseListFilters) =>
  infiniteQueryOptions({
    queryKey: ['cases', 'list', 'pages', filters] as const,
    queryFn: ({ pageParam, signal }) => {
      const query: CaseListQuery = {
        limit: CASE_PAGE_SIZE,
        ...(filters.view === 'waiting' ? { attention: 'me' as const } : {}),
        ...(filters.view === 'mine' ? { createdBy: 'me' as const } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.domain ? { domain: filters.domain } : {}),
        ...(filters.state ? { state: filters.state } : {}),
        ...(filters.processType ? { processType: filters.processType } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      };
      return client.call(operatorApi.cases.list, { query, signal });
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
  });

/**
 * 21d: open work items and pending approval requests the viewer may see, for the deadline badges of the case list
 * (refreshed with the list).
 */
export const openDeadlinesQuery = queryOptions({
  queryKey: ['cases', 'deadlines'] as const,
  queryFn: async ({ signal }) => {
    const [open, claimed, requests] = await Promise.all([
      client.call(operatorApi.workItems.list, { query: { state: 'open', limit: 500 }, signal }),
      client.call(operatorApi.workItems.list, { query: { state: 'claimed', limit: 500 }, signal }),
      client.call(operatorApi.approvalRequests.list, { query: { status: 'pending' }, signal }),
    ]);
    return { items: [...open.items, ...claimed.items], requests: requests.items };
  },
  refetchInterval: 15_000,
  meta: { silent: true },
});

/** The case detail; with `activationId` the server embeds that agent activity's episode (20a). */
export const caseDetailQuery = (caseId: string, activationId?: string) =>
  queryOptions({
    queryKey: caseKeys.detailWith(caseId, activationId),
    queryFn: ({ signal }) =>
      client.call(operatorApi.cases.get, {
        params: { caseId },
        query: activationId ? { activity: activationId } : {},
        signal,
      }),
  });

export const caseTimelineQuery = (caseId: string) =>
  queryOptions({
    queryKey: caseKeys.timeline(caseId),
    queryFn: ({ signal }) => client.call(operatorApi.cases.timeline, { params: { caseId }, signal }),
  });

/**
 * Open and claimed work items of the case (review B1: completed items are never fetched; the server also returns
 * `output` only to holders of the item's role or `act` on its scope).
 */
export const caseWorkItemsQuery = (caseId: string) =>
  queryOptions({
    queryKey: caseKeys.workItems(caseId),
    queryFn: async ({ signal }) => {
      const [open, claimed] = await Promise.all(
        (['open', 'claimed'] as const).map((state) =>
          client.call(operatorApi.workItems.list, { query: { caseId, state }, signal }),
        ),
      );
      return { items: [...(open?.items ?? []), ...(claimed?.items ?? [])] };
    },
  });

/** Inbound records of one case (operator only: the route admits operator and admin; server-side `caseId`). */
export const inboundRecordsQuery = (caseId: string) =>
  queryOptions({
    queryKey: caseKeys.inbound(caseId),
    queryFn: ({ signal }) => client.call(operatorApi.inbound.list, { query: { caseId, limit: 200 }, signal }),
    meta: { silent: true },
  });

/**
 * One process instance with its budget use. The case detail lists instances without `budgetUsage`; only the
 * instance route computes it (17e abstraction defect D-17e-3), so the page reads each instance once more.
 */
export const processInstanceQuery = (processInstanceId: string) =>
  queryOptions({
    queryKey: caseKeys.instance(processInstanceId),
    queryFn: ({ signal }) =>
      client.call(operatorApi.processes.get, { params: { processInstanceId }, signal }),
    meta: { silent: true },
  });

export const processDefinitionsQuery = queryOptions({
  queryKey: caseKeys.definitions,
  queryFn: ({ signal }) => client.call(operatorApi.processes.definitions, { signal }),
  staleTime: 5 * 60_000,
});

export const myEntitlementsQuery = queryOptions({
  queryKey: caseKeys.entitlements,
  queryFn: ({ signal }) => client.call(operatorApi.cases.entitlements, { signal }),
  staleTime: 60_000,
  meta: { silent: true },
});

/** The mutations the case page offers; each resolves to the route's response. */
export const caseActions = {
  claim: (workItemId: string) => client.call(operatorApi.workItems.claim, { params: { workItemId } }),
  complete: (workItemId: string, output: Record<string, unknown>) =>
    client.call(operatorApi.workItems.complete, { params: { workItemId }, body: { output } }),
  cancelWork: (workItemId: string, reason: string) =>
    client.call(operatorApi.workItems.cancel, { params: { workItemId }, body: { reason } }),
  reconcile: (operationId: string) =>
    client.call(operatorApi.operations.reconcile, { params: { operationId } }),
  release: (operationId: string, reason: string, reversalRef: string) =>
    client.call(operatorApi.operations.release, { params: { operationId }, body: { reason, reversalRef } }),
  replay: (inboxId: string, reason: string) =>
    client.call(operatorApi.inbound.replay, { params: { inboxId }, body: { reason } }),
  cancelCase: (caseId: string, reason: string) =>
    client.call(operatorApi.cases.cancel, { params: { caseId }, body: { reason } }),
  /** Answers the questions of a waiting agent activity (20a). */
  clarify: (caseId: string, activationId: string, answers: Record<string, unknown>) =>
    client.call(operatorApi.cases.clarifyActivity, { params: { caseId, activationId }, body: { answers } }),
  /**
   * 21d: the work item with its served output schema (`WorkItemView` once 21b switches the descriptor; until then the
   * client parses `WorkItem` and the dialog falls back to the JSON field).
   */
  workItem: (workItemId: string) => client.call(operatorApi.workItems.get, { params: { workItemId } }),
  /** 21d: boundary hold and resume (21a). */
  hold: (caseId: string, body: HoldRequestInput) =>
    client.call(wave21OperatorRoutes.holdCase, { params: { caseId }, body }),
  resume: (caseId: string, body: { evidence: string[]; note?: string }) =>
    client.call(wave21OperatorRoutes.resumeCase, { params: { caseId }, body }),
  /** 21d: documents (21c). */
  uploadDocument: (caseId: string, file: File, meta: UploadDocumentMeta) =>
    uploadCaseDocument(client, caseId, file, meta),
  verifyDocument: (documentId: string, body: VerifyDocumentBody) =>
    client.call(wave21OperatorRoutes.verifyDocument, { params: { documentId }, body }),
  /** document-delete: retire with the bytes removed (`DELETE /documents/{id}`). */
  deleteDocument: (documentId: string, body: DeleteDocumentBody) =>
    client.call(wave21OperatorRoutes.deleteDocument, { params: { documentId }, body }),
  documentHref: (documentId: string) =>
    client.url(wave21OperatorRoutes.documentContent, { params: { documentId } }),
  /** 21d: obligations and the evidence export (21c). */
  updateObligation: (caseId: string, obligationId: string, body: UpdateObligationBody) =>
    client.call(wave21OperatorRoutes.updateObligation, { params: { caseId, obligationId }, body }),
  exportEvidence: (caseId: string) => downloadEvidence(caseId),
  /** Cancels a running agent activity (20a). */
  cancelActivity: (caseId: string, activationId: string, reason: string) =>
    client.call(operatorApi.cases.cancelActivity, { params: { caseId, activationId }, body: { reason } }),
};

/** `POST /cases`: starts a process or an agent (a one-activity case). */
export function startCase(body: StartCaseBody) {
  return client.call(operatorApi.cases.start, { body });
}

// -----------------------------------------------------------------------------------------------
// 26c: obligations of outcome-driven cases (brief 26 §3.5, §6.4)
// -----------------------------------------------------------------------------------------------

/** `GET /me/obligations`: the viewer's obligations (owner role, assignee, respondent), overdue first, then due date. */
export const myObligationsQuery = (state: ObligationStatus = 'open') =>
  queryOptions({
    queryKey: caseKeys.myObligations(state),
    queryFn: ({ signal }) =>
      client.call(operatorApi.obligations.myObligations, { query: { state, limit: 200 }, signal }),
    refetchInterval: 15_000,
  });

/** The four obligation routes, as plain calls (each resolves to the route's response). */
export const obligationActions = {
  answer: (caseId: string, obligationId: string, body: AnswerObligationBody) =>
    client.call(operatorApi.obligations.answerObligation, { params: { caseId, obligationId }, body }),
  waive: (caseId: string, obligationId: string, reason: string) =>
    client.call(operatorApi.obligations.waiveObligation, {
      params: { caseId, obligationId },
      body: { reason },
    }),
  decide: (caseId: string, obligationId: string, body: DecideObligationBody) =>
    client.call(operatorApi.obligations.decideObligation, { params: { caseId, obligationId }, body }),
  askAgain: (caseId: string, obligationId: string, body: AskAgainObligationBody = {}) =>
    client.call(operatorApi.obligations.askAgainObligation, { params: { caseId, obligationId }, body }),
};

/** What changes after an obligation call: the case (detail and timeline), the case lists and "Waiting on you". */
export function invalidateAfterObligationChange(qc: QueryClient, caseId: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
    qc.invalidateQueries({ queryKey: ['cases', 'list'] }),
    qc.invalidateQueries({ queryKey: ['me', 'obligations'] }),
  ]).then(() => undefined);
}

/** An answer body without its key: the submitter adds `idempotencyKey`. */
export type AnswerDraft = Omit<AnswerObligationBody, 'idempotencyKey'>;

/**
 * One answer form's submit attempts (§6.4, §7): every attempt carries an `idempotencyKey`. A failed attempt keeps its
 * key only when resending the same answer is the next step: a network error or a 5xx (Retry), or a 400
 * `validation_failed` with field issues (the respondent fixes the fields and resends). Any other refusal (409
 * `obligation_settled`, 403 `forbidden`, 409 `idempotency_conflict`, 400 `decision_only`, …) and every success rotate
 * the key, so the next answer (e.g. Change answer after the 409) is a new attempt that the inbox does not dedupe
 * against the refused one.
 */
export interface AnswerSubmitter {
  /** The key the next `submit` sends. */
  readonly key: string;
  submit(draft: AnswerDraft): Promise<AnswerObligationResponse>;
  /** Starts a fresh attempt (an answer form opens). */
  reset(): void;
}

/** A fresh idempotency key (`answer-<uuid>`). */
export function newAnswerKey(): string {
  return `answer-${crypto.randomUUID()}`;
}

/**
 * True when a failed answer attempt keeps its idempotency key (the resend is the same attempt): anything that is not
 * a platform error (transport), a 5xx or retryable error, and a 400 `validation_failed` with field issues.
 */
export function keepsAnswerKey(error: unknown): boolean {
  if (!isPlatformError(error)) return true;
  if (error.code === 'validation_failed') return error.details?.reason !== 'decision_only';
  const status = ERROR_HTTP_STATUS[error.code] as number | undefined;
  return status === undefined || status >= 500 || error.retryable;
}

/** Builds an `AnswerSubmitter` over `send` (tests pass a fake `send` and `newKey`). */
export function createAnswerSubmitter(
  send: (body: AnswerObligationBody) => Promise<AnswerObligationResponse>,
  newKey: () => string = newAnswerKey,
): AnswerSubmitter {
  let key = newKey();
  return {
    get key() {
      return key;
    },
    async submit(draft) {
      const sent = key;
      try {
        const response = await send({ ...draft, idempotencyKey: sent });
        if (key === sent) key = newKey();
        return response;
      } catch (error) {
        if (key === sent && !keepsAnswerKey(error)) key = newKey();
        throw error;
      }
    },
    reset() {
      key = newKey();
    },
  };
}

/** True when a failed answer means the case changed under the viewer (409 `obligation_settled`): reload it. */
export function answerErrorChangesCase(error: unknown): boolean {
  return isPlatformError(error) && error.code === 'obligation_settled';
}

/**
 * Mutations of the obligation cards of one case. `answer` keeps one `AnswerSubmitter` per obligation for the life of
 * the component (same key on retry; the card resets it when a form opens); every success invalidates the case, the
 * lists and "Waiting on you", and so does a 409 `obligation_settled` answer (the card then shows the answer that counts
 * and Change answer). Errors are left to the caller (`obligationErrorNotice`), so the mutations are `silent` for the
 * global error toast.
 */
export function useObligationMutations(caseId: string) {
  const qc = useQueryClient();
  const submitters = useRef(new Map<string, AnswerSubmitter>());
  const submitterFor = (obligationId: string): AnswerSubmitter => {
    let s = submitters.current.get(obligationId);
    if (!s) {
      s = createAnswerSubmitter((body) => obligationActions.answer(caseId, obligationId, body));
      submitters.current.set(obligationId, s);
    }
    return s;
  };
  const onSuccess = () => invalidateAfterObligationChange(qc, caseId);
  const meta = { silent: true };
  return {
    submitterFor,
    answer: useMutation({
      mutationFn: ({ obligationId, draft }: { obligationId: string; draft: AnswerDraft }) =>
        submitterFor(obligationId).submit(draft),
      onSuccess,
      onError: (error) => (answerErrorChangesCase(error) ? onSuccess() : undefined),
      meta,
    }),
    waive: useMutation({
      mutationFn: ({ obligationId, reason }: { obligationId: string; reason: string }) =>
        obligationActions.waive(caseId, obligationId, reason),
      onSuccess,
      meta,
    }),
    decide: useMutation({
      mutationFn: ({ obligationId, body }: { obligationId: string; body: DecideObligationBody }) =>
        obligationActions.decide(caseId, obligationId, body),
      onSuccess,
      meta,
    }),
    askAgain: useMutation({
      mutationFn: ({ obligationId, body }: { obligationId: string; body?: AskAgainObligationBody }) =>
        obligationActions.askAgain(caseId, obligationId, body),
      onSuccess,
      meta,
    }),
  };
}
